import type { Domain } from "../../domain/contracts";
import { createVisitedIslandCachePlan } from "./islandCachePolicy";
import type { CacheFailureReason } from "./types";

const FALLBACK_APP_ORIGIN = "https://little-launch-island.invalid";

export interface VisitedIslandWritableCache {
  add(resource: RequestInfo | URL): Promise<void>;
}

export interface VisitedIslandCacheStorage {
  open(cacheName: string): Promise<VisitedIslandWritableCache>;
}

export interface CacheVisitedIslandOptions {
  readonly domain: Domain;
  readonly resources: readonly string[];
  readonly appOrigin?: string;
  readonly cacheStorage?: VisitedIslandCacheStorage;
  readonly onCacheFailure?: (reason: CacheFailureReason) => void;
  readonly onCacheReady?: () => void;
}

export interface CacheVisitedIslandResult {
  readonly cacheName: string;
  readonly isComplete: boolean;
  readonly cachedResources: readonly string[];
  readonly skippedResources: readonly string[];
  readonly failedResources: readonly string[];
  readonly failureReason: CacheFailureReason | null;
}

export async function cacheVisitedIslandResources({
  domain,
  resources,
  appOrigin = readAppOrigin(),
  cacheStorage = readBrowserCacheStorage(),
  onCacheFailure,
  onCacheReady
}: CacheVisitedIslandOptions): Promise<CacheVisitedIslandResult> {
  const plan = createVisitedIslandCachePlan(domain, resources, appOrigin);

  if (cacheStorage === undefined) {
    const result = createFailedResult(
      plan.policy.cacheName,
      plan.resources,
      plan.skippedResources,
      "registration"
    );
    onCacheFailure?.(result.failureReason);
    return result;
  }

  let cache: VisitedIslandWritableCache;

  try {
    cache = await cacheStorage.open(plan.policy.cacheName);
  } catch (error) {
    const failureReason = isQuotaError(error) ? "quota" : "registration";
    const result = createFailedResult(
      plan.policy.cacheName,
      plan.resources,
      plan.skippedResources,
      failureReason
    );
    onCacheFailure?.(failureReason);
    return result;
  }

  const cachedResources: string[] = [];
  const failedResources: string[] = [];
  let failureReason: CacheFailureReason | null = null;

  for (let index = 0; index < plan.resources.length; index += 1) {
    const resource = plan.resources[index];

    if (resource === undefined) {
      continue;
    }

    try {
      await cache.add(resource);
      cachedResources.push(resource);
    } catch (error) {
      const resourceFailureReason = isQuotaError(error) ? "quota" : "download";
      failureReason = mergeFailureReasons(failureReason, resourceFailureReason);
      failedResources.push(resource);

      if (resourceFailureReason === "quota") {
        failedResources.push(...plan.resources.slice(index + 1));
        break;
      }
    }
  }

  const result: CacheVisitedIslandResult = {
    cacheName: plan.policy.cacheName,
    isComplete: failureReason === null,
    cachedResources,
    skippedResources: plan.skippedResources,
    failedResources,
    failureReason
  };

  if (failureReason === null) {
    onCacheReady?.();
  } else {
    onCacheFailure?.(failureReason);
  }

  return result;
}

function createFailedResult(
  cacheName: string,
  resources: readonly string[],
  skippedResources: readonly string[],
  failureReason: CacheFailureReason
): CacheVisitedIslandResult & { readonly failureReason: CacheFailureReason } {
  return {
    cacheName,
    isComplete: false,
    cachedResources: [],
    skippedResources,
    failedResources: resources,
    failureReason
  };
}

function mergeFailureReasons(
  current: CacheFailureReason | null,
  next: CacheFailureReason
): CacheFailureReason {
  return current === "quota" || next === "quota" ? "quota" : next;
}

function isQuotaError(error: unknown): boolean {
  return (
    typeof error === "object" &&
    error !== null &&
    Reflect.get(error, "name") === "QuotaExceededError"
  );
}

function readAppOrigin(): string {
  return typeof location === "undefined" ? FALLBACK_APP_ORIGIN : location.origin;
}

function readBrowserCacheStorage(): VisitedIslandCacheStorage | undefined {
  return typeof caches === "undefined" ? undefined : caches;
}
